import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  Grid,
  GridItem,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  TextInput
} from '@patternfly/react-core';
import * as React from 'react';

import { COURSE_STATUS, Course } from '@app/model/Course';
import { CourseVersion } from '@app/model/CourseVersion';
import { HARDWARE_LEVEL } from '@app/model/Enums';
import { dateFormater, ddMMyyyyParse, fromIsoDate, toIsoDate } from '@app/utils/functions';

/**
 * The draft the form holds while it is open.
 *
 * Dates are kept as the "yyyy-MM-dd" the API takes rather than as Date objects, so that a
 * half-typed one is a string the picker can keep showing instead of an Invalid Date the
 * formatter would wipe on the next render. Same arrangement as the Holidays form.
 *
 * `duration` and `hoursDay` are strings for the same reason: a number input mid-edit is
 * legitimately empty, and parsing on every keystroke turns a cleared field into NaN.
 */
interface VersionDraft {
  version: string;
  extendedSku: string;
  name: string;
  status: COURSE_STATUS;
  releaseDate: string;
  legacyDate: string;
  retirementDate: string;
  duration: string;
  hoursDay: string;
  hardwareLevel: HARDWARE_LEVEL;
  certificationAvailable: boolean;
}

/** A stored date as the draft holds it. The API answers "yyyy-MM-dd", which is already it. */
const draftDate = (value: Date | string | undefined | null): string => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  return toIsoDate(value);
};

/** What the picker shows for a draft date: "dd/MM/yyyy", or nothing at all for an unset one. */
const pickerValue = (iso: string): string => (iso ? dateFormater(fromIsoDate(iso)) : '');

const draftOf = (version: CourseVersion | null): VersionDraft => ({
  version: version?.version ?? '',
  extendedSku: version?.extendedSku ?? '',
  name: version?.name ?? '',
  status: version?.status ?? COURSE_STATUS.ACTIVE,
  releaseDate: draftDate(version?.releaseDate),
  legacyDate: draftDate(version?.legacyDate),
  retirementDate: draftDate(version?.retirementDate),
  duration: version?.duration === undefined || version?.duration === null ? '' : String(version.duration),
  hoursDay: version?.hoursDay === undefined || version?.hoursDay === null ? '' : String(version.hoursDay),
  hardwareLevel: version?.hardwareLevel ?? HARDWARE_LEVEL.NOT_APPLICABLE,
  certificationAvailable: version?.certificationAvailable ?? false
});

/** What is missing or malformed, keyed by field so each one can be marked where it sits. */
const problemsWith = (draft: VersionDraft): Record<string, string> => {
  const problems: Record<string, string> = {};
  if (!draft.version.trim()) problems.version = 'Version is required.';
  if (!draft.extendedSku.trim()) problems.extendedSku = 'Extended SKU is required.';
  if (draft.duration.trim() && !(Number(draft.duration) > 0)) {
    problems.duration = 'Duration is a number of days greater than zero.';
  }
  if (draft.hoursDay.trim() && !(Number(draft.hoursDay) > 0)) {
    problems.hoursDay = 'Hours per day is a number greater than zero.';
  }
  // A version cannot be withdrawn before it was published. Either date alone is fine: a version
  // that is still current has neither, and one going legacy has no retirement date yet.
  if (draft.releaseDate && draft.retirementDate && draft.retirementDate < draft.releaseDate) {
    problems.retirementDate = 'Retirement cannot precede the release.';
  }
  if (draft.releaseDate && draft.legacyDate && draft.legacyDate < draft.releaseDate) {
    problems.legacyDate = 'Going legacy cannot precede the release.';
  }
  return problems;
};

export interface CourseVersionModalProps {
  isOpen: boolean;
  /** The version being edited, or null to create one. */
  version: CourseVersion | null;
  /** The course it belongs to: its SKU seeds the extended SKU, and an exam has no
   * certification of its own to offer. */
  course: Course;
  onClose(): void;
  /** Hands back what to store. The page owns the request, as it owns the refresh after it. */
  onSave(payload: Partial<CourseVersion>): void | Promise<void>;
}

/**
 * The form for one course version, as a modal rather than as an editable table row.
 *
 * Eleven fields - three of them dates - do not fit a row the way a facility's five do, and the
 * three that the table has no column for (the version's own name, the legacy and retirement
 * dates) would otherwise be unreachable from the only screen that lists versions at all.
 *
 * Offered to administrators only; see README-permissions.md. The page decides that, not this
 * component - it is never rendered for anybody else.
 */
export const CourseVersionModal: React.FunctionComponent<CourseVersionModalProps> = ({
  isOpen,
  version,
  course,
  onClose,
  onSave
}) => {
  const [draft, setDraft] = React.useState<VersionDraft>(() => draftOf(version));
  /** Nothing is marked red before the user has asked for the record to be saved. */
  const [saveAttempted, setSaveAttempted] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const isNew = !version;

  // Re-seeded whenever the modal opens, and on the row it opens over: the component stays
  // mounted between openings, so without this the second edit would show the first one's draft.
  React.useEffect(() => {
    if (!isOpen) return;
    setDraft(draftOf(version));
    setSaveAttempted(false);
  }, [isOpen, version]);

  const problems = problemsWith(draft);
  const marked = (field: string) => (saveAttempted && problems[field] ? 'error' : 'default');

  const set = <K extends keyof VersionDraft>(field: K, value: VersionDraft[K]) =>
    setDraft((current) => ({ ...current, [field]: value }));

  /**
   * The extended SKU follows the version unless it has been typed over: it is the course's SKU
   * and the version number, and filling it in by hand for every new version is a transcription
   * error waiting to happen. Only on creation - an extended SKU already stored is what other
   * records quote, so it is never rewritten under the user.
   */
  const onVersionChange = (value: string) => {
    setDraft((current) => {
      const derived = `${course.sku}-${current.version}`;
      const keepsFollowing = isNew && (current.extendedSku === '' || current.extendedSku === derived);
      return {
        ...current,
        version: value,
        extendedSku: keepsFollowing ? `${course.sku}-${value}` : current.extendedSku
      };
    });
  };

  const onDateChange = (field: 'releaseDate' | 'legacyDate' | 'retirementDate') =>
    (_event: React.FormEvent<HTMLInputElement>, value: string, date?: Date) => {
      // Only replaced once a real date comes back, so a half-typed one is not wiped
      // mid-keystroke; clearing the field clears the record.
      if (date && !isNaN(date.getTime())) set(field, toIsoDate(date));
      else if (!value) set(field, '');
    };

  const submit = async () => {
    setSaveAttempted(true);
    if (Object.keys(problems).length > 0) return;

    setIsSaving(true);
    try {
      await onSave({
        // Carries the id and entityVersion of the record being edited: the service's
        // optimistic lock rejects a write that does not quote the revision it read.
        ...(version ?? {}),
        version: draft.version.trim(),
        extendedSku: draft.extendedSku.trim(),
        name: draft.name.trim(),
        status: draft.status,
        releaseDate: (draft.releaseDate || null) as unknown as Date,
        legacyDate: (draft.legacyDate || null) as unknown as Date,
        retirementDate: (draft.retirementDate || null) as unknown as Date,
        duration: draft.duration.trim() ? Number(draft.duration) : (null as unknown as number),
        hoursDay: draft.hoursDay.trim() ? Number(draft.hoursDay) : (null as unknown as number),
        hardwareLevel: draft.hardwareLevel,
        // An exam is the certification, so it never offers one of its own
        certificationAvailable: course.isCertification ? false : draft.certificationAvailable
      });
    } finally {
      setIsSaving(false);
    }
  };

  const helper = (field: string, hint?: string) => (
    <FormHelperText>
      <HelperText>
        <HelperTextItem variant={marked(field)}>
          {(saveAttempted && problems[field]) || hint}
        </HelperTextItem>
      </HelperText>
    </FormHelperText>
  );

  const dateField = (
    field: 'releaseDate' | 'legacyDate' | 'retirementDate',
    label: string,
    hint: string
  ) => (
    <GridItem span={4}>
      <FormGroup label={label} fieldId={field}>
        {/* dd/MM/yyyy in the field, "yyyy-MM-dd" in the record: the format pair has to be
            given explicitly, or the input renders in one format and parses in another. */}
        <DatePicker
          id={field}
          value={pickerValue(draft[field])}
          onChange={onDateChange(field)}
          dateFormat={dateFormater}
          dateParse={ddMMyyyyParse}
          placeholder="DD/MM/YYYY"
          appendTo={() => document.body}
          aria-label={label}
        />
        {helper(field, hint)}
      </FormGroup>
    </GridItem>
  );

  return (
    <Modal
      variant={ModalVariant.medium}
      isOpen={isOpen}
      onClose={onClose}
      aria-label={isNew ? 'New course version' : `Edit version ${version?.version}`}
    >
      <ModalHeader
        title={isNew ? 'New course version' : `Version ${version?.version}`}
        description={`${course.sku} — ${course.name}`}
      />
      <ModalBody>
        {saveAttempted && Object.keys(problems).length > 0 && (
          <Alert
            isInline
            variant="danger"
            title="Fill out all required fields before saving."
            aria-live="polite"
            style={{ marginBottom: 'var(--pf-t--global--spacer--md)' }}
          />
        )}
        <Form>
          <Grid hasGutter>
            <GridItem span={4}>
              <FormGroup label="Version" isRequired fieldId="version">
                <TextInput
                  id="version"
                  value={draft.version}
                  validated={marked('version')}
                  onChange={(_e, value) => onVersionChange(value)}
                  aria-label="Version"
                  placeholder="e.g. 9.0"
                />
                {helper('version')}
              </FormGroup>
            </GridItem>
            <GridItem span={4}>
              <FormGroup label="Extended SKU" isRequired fieldId="extendedSku">
                <TextInput
                  id="extendedSku"
                  value={draft.extendedSku}
                  validated={marked('extendedSku')}
                  onChange={(_e, value) => set('extendedSku', value)}
                  aria-label="Extended SKU"
                  placeholder={`e.g. ${course.sku}-9.0`}
                />
                {helper('extendedSku', 'Follows the version until you type over it.')}
              </FormGroup>
            </GridItem>
            <GridItem span={4}>
              <FormGroup label="Status" fieldId="versionStatus">
                <FormSelect
                  id="versionStatus"
                  value={draft.status}
                  onChange={(_e, value) => set('status', value as COURSE_STATUS)}
                  aria-label="Status"
                >
                  {Object.values(COURSE_STATUS).map((status) => (
                    <FormSelectOption key={status} value={status} label={status.replace(/_/g, ' ')} />
                  ))}
                </FormSelect>
              </FormGroup>
            </GridItem>

            <GridItem span={12}>
              <FormGroup label="Name" fieldId="versionName">
                <TextInput
                  id="versionName"
                  value={draft.name}
                  onChange={(_e, value) => set('name', value)}
                  aria-label="Version name"
                />
                {helper('versionName', 'What this version is called, where it differs from the course.')}
              </FormGroup>
            </GridItem>

            {dateField('releaseDate', 'Release date', 'When it became available.')}
            {dateField('legacyDate', 'Legacy date', 'When it stopped being the current one.')}
            {dateField('retirementDate', 'Retirement date', 'When it stopped being delivered.')}

            <GridItem span={4}>
              <FormGroup label="Duration (days)" fieldId="duration">
                {/* step="any": half-day courses are ordinary - the catalogue holds 0.5 - and the
                    default step of 1 marks a fractional value invalid in the browser. */}
                <TextInput
                  id="duration"
                  type="number"
                  min={0}
                  step="any"
                  value={draft.duration}
                  validated={marked('duration')}
                  onChange={(_e, value) => set('duration', value)}
                  aria-label="Duration in days"
                />
                {helper('duration', 'Half days are allowed, e.g. 0.5.')}
              </FormGroup>
            </GridItem>
            <GridItem span={4}>
              <FormGroup label="Hours per day" fieldId="hoursDay">
                <TextInput
                  id="hoursDay"
                  type="number"
                  min={0}
                  step="any"
                  value={draft.hoursDay}
                  validated={marked('hoursDay')}
                  onChange={(_e, value) => set('hoursDay', value)}
                  aria-label="Hours per day"
                />
                {helper('hoursDay')}
              </FormGroup>
            </GridItem>
            <GridItem span={4}>
              <FormGroup label="Hardware level" fieldId="hardwareLevel">
                <FormSelect
                  id="hardwareLevel"
                  value={draft.hardwareLevel}
                  onChange={(_e, value) => set('hardwareLevel', value as HARDWARE_LEVEL)}
                  aria-label="Hardware level"
                >
                  {Object.values(HARDWARE_LEVEL).map((level) => (
                    <FormSelectOption key={level} value={level} label={level.replace(/_/g, ' ')} />
                  ))}
                </FormSelect>
              </FormGroup>
            </GridItem>

            {/* An exam certifies nothing further, so the question is not put to one. */}
            {!course.isCertification && (
              <GridItem span={12}>
                <FormGroup fieldId="certificationAvailable">
                  <Checkbox
                    id="certificationAvailable"
                    label="A certification is available for this version"
                    isChecked={draft.certificationAvailable}
                    onChange={(_e, checked) => set('certificationAvailable', checked)}
                  />
                </FormGroup>
              </GridItem>
            )}
          </Grid>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={() => void submit()} isLoading={isSaving} isDisabled={isSaving}>
          {isNew ? 'Create version' : 'Save version'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
